import { ROLE } from "../data/enums";
import { ROUTES } from "./routes";

export type SidebarItemKey =
    | "dashboard"
    | "products"
    | "materials"
    | "extraFees"
    | "saleOrders"
    | "materialOrders"
    | "materialRequests"
    | "outsourceOrders"
    | "deliveryTrips"
    | "customers"
    | "providers"
    | "users"
    | "kpis"
    | "files";

export interface SidebarRoleAccessItem {
    key: SidebarItemKey;
    label: string;
    route: string;
    routeGroup?: string[];
    allowedRoles: ROLE[];
}

export const SIDEBAR_ROLE_GROUPS = {
    managers: [ROLE.MANAGER],
    logisticManagers: [ROLE.LOGISTIC_MANAGER],
    saleStaff: [ROLE.SALE_STAFF],
    productionCoordinators: [ROLE.PRODUCTION_STAFF],
    purchasingStaff: [ROLE.PURCHASING_STAFF],
    storageKeepers: [
        ROLE.STORAGE_KEEPER,
        ROLE.FIRST_STORAGE_KEEPER,
        ROLE.SECOND_STORAGE_KEEPER,
    ],
    factoryStaff: [
        ROLE.FACTORY_STAFF,
        ROLE.FACTORY_MANAGER_ONE,
        ROLE.FACTORY_MANAGER_TWO,
        ROLE.DEPUTY_FACTORY_MANAGER_ONE,
        ROLE.DEPUTY_FACTORY_MANAGER_TWO,
    ],
};

const combineRoles = (...roleGroups: ROLE[][]): ROLE[] =>
    Array.from(
        new Set(
            roleGroups.reduce<ROLE[]>(
                (roles, roleGroup) => roles.concat(roleGroup),
                [],
            ),
        ),
    );

const {
    managers,
    logisticManagers,
    saleStaff,
    productionCoordinators,
    purchasingStaff,
    storageKeepers,
    factoryStaff,
} = SIDEBAR_ROLE_GROUPS;

const allMiniAppRoles = combineRoles(
    managers,
    logisticManagers,
    saleStaff,
    productionCoordinators,
    purchasingStaff,
    storageKeepers,
    factoryStaff,
);

const productRoles = combineRoles(
    managers,
    logisticManagers,
    saleStaff,
    productionCoordinators,
    storageKeepers,
    factoryStaff,
);

const materialRoles = combineRoles(
    managers,
    logisticManagers,
    saleStaff,
    purchasingStaff,
    storageKeepers,
    factoryStaff,
);

const costRoles = combineRoles(managers, logisticManagers, saleStaff);

const purchasingWorkflowRoles = combineRoles(
    managers,
    logisticManagers,
    productionCoordinators,
    purchasingStaff,
    storageKeepers,
    factoryStaff,
);

const providerRoles = purchasingWorkflowRoles;
const kpiRoles = combineRoles(managers, logisticManagers);

export const SIDEBAR_ROLE_ACCESS: Record<
    SidebarItemKey,
    SidebarRoleAccessItem
> = {
    dashboard: {
        key: "dashboard",
        label: "Tổng quan",
        route: ROUTES.Dashboard,
        allowedRoles: allMiniAppRoles,
    },
    products: {
        key: "products",
        label: "Sản phẩm",
        route: ROUTES["Sản phẩm"],
        allowedRoles: productRoles,
    },
    materials: {
        key: "materials",
        label: "Vật tư",
        route: ROUTES["Vật tư"],
        allowedRoles: materialRoles,
    },
    extraFees: {
        key: "extraFees",
        label: "Chi phí",
        route: ROUTES["Chi phí kèm theo"],
        allowedRoles: costRoles,
    },
    saleOrders: {
        key: "saleOrders",
        label: "Bán hàng",
        route: ROUTES["Bán hàng"],
        allowedRoles: allMiniAppRoles,
    },
    materialOrders: {
        key: "materialOrders",
        label: "Mua hàng",
        route: ROUTES["Mua hàng"],
        allowedRoles: purchasingWorkflowRoles,
    },
    materialRequests: {
        key: "materialRequests",
        label: "Xưởng sản xuất",
        route: ROUTES["Xưởng sản xuất"],
        allowedRoles: purchasingWorkflowRoles,
    },
    outsourceOrders: {
        key: "outsourceOrders",
        label: "Đơn gia công",
        route: ROUTES["Đơn hàng gia công"],
        allowedRoles: purchasingWorkflowRoles,
    },
    deliveryTrips: {
        key: "deliveryTrips",
        label: "Kế hoạch vận chuyển",
        route: ROUTES["Kế hoạch vận chuyển"],
        allowedRoles: allMiniAppRoles,
    },
    customers: {
        key: "customers",
        label: "Khách hàng",
        route: ROUTES["Khách hàng"],
        allowedRoles: allMiniAppRoles,
    },
    providers: {
        key: "providers",
        label: "Nhà cung cấp",
        route: ROUTES["Nhà cung cấp"],
        allowedRoles: providerRoles,
    },
    users: {
        key: "users",
        label: "Nhân viên",
        route: ROUTES["Nhân viên"],
        allowedRoles: allMiniAppRoles,
    },
    kpis: {
        key: "kpis",
        label: "Quản lý chỉ tiêu",
        route: ROUTES["Quản lý chỉ tiêu"],
        allowedRoles: kpiRoles,
    },
    files: {
        key: "files",
        label: "Quản lý tệp tải lên",
        route: ROUTES["Quản lý tệp tải lên"],
        allowedRoles: allMiniAppRoles,
    },
};

const validRoles = new Set<ROLE>(Object.values(ROLE) as ROLE[]);

export const canViewSidebarItem = (
    userRole: ROLE | string | null | undefined,
    item: Pick<SidebarRoleAccessItem, "allowedRoles">,
) => {
    if (!userRole || !validRoles.has(userRole as ROLE)) {
        return false;
    }

    return item.allowedRoles.includes(userRole as ROLE);
};
